<template>
  <div
    class="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center gap-[15px] pointer-events-none select-none [-webkit-touch-callout:none]"
    :style="{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }"
  >
    <div
      class="pointer-events-auto relative h-[62px] touch-none rounded-full bg-white/70 backdrop-blur-xl shadow-[0_3px_28px_0_rgba(0,0,0,0.10)]"
      :style="{ width: `${barWidth}px` }"
    >
      <div
        ref="capsule"
        class="pointer-events-none absolute top-1 left-1 h-[54px] rounded-[27px] bg-[#787880]/16 transition-opacity duration-150 will-change-transform"
        :class="displayIndex < 0 ? 'opacity-0' : 'opacity-100'"
        :style="{ width: `${SELECTION_WIDTH}px` }"
      />
      <div
        class="ml-1 flex h-[62px] items-center justify-center"
        :style="{ width: `${barWidth - BAR_PADDING * 2}px` }"
      >
        <button
          v-for="(tab, index) in tabs"
          :key="tab.key"
          type="button"
          role="tab"
          :aria-label="tab.label"
          :aria-selected="displayIndex === index"
          class="relative flex h-[54px] cursor-pointer flex-col items-center justify-center [-webkit-tap-highlight-color:transparent]"
          :style="{ width: `${TAB_PITCH}px` }"
          @pointerdown="handlePress($event, index)"
          @pointerup="handleRelease($event, index)"
          @pointercancel="releasePress"
          @click="handleClick($event, index)"
        >
          <span class="relative size-[22px]">
            <component
              :is="tab.activeIcon"
              class="absolute inset-0 size-[22px] text-green-900"
              :class="displayIndex === index ? 'opacity-100' : 'opacity-0'"
            />
            <component
              :is="tab.icon"
              class="absolute inset-0 size-[22px] text-[#404040]"
              :class="displayIndex === index ? 'opacity-0' : 'opacity-100'"
            />
            <span
              v-if="tab.badge && hasUnread"
              class="absolute -top-[3px] -right-[3px] size-[13px] rounded-full border-[1.5px] border-white bg-red-700"
            />
          </span>
          <span
            class="mt-px self-stretch text-center text-[10px] leading-[13px] font-medium"
            :class="
              displayIndex === index ? 'text-green-900' : 'text-[#404040]'
            "
          >
            {{ tab.label }}
          </span>
        </button>
      </div>
    </div>

    <button
      ref="fab"
      type="button"
      class="pointer-events-auto flex size-[50px] items-center justify-center rounded-full bg-green-900 shadow-[0_3px_28px_0_rgba(0,0,0,0.10)]"
      :class="{
        'pointer-events-none opacity-50': !aiEnabled && !canStartUpload,
      }"
      aria-label="Add"
      @click="handleUpload"
    >
      <FileAddFilledIcon class="size-[24px] text-white" />
    </button>

    <UploadMenu v-model:open="menuOpen" :origin="fab ?? null" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  HomeIcon,
  HomeFilledIcon,
  FileDocketIcon,
  FileDocketFilledIcon,
  BellIcon,
  BellFilledIcon,
  FileAddFilledIcon,
} from '@dacr/icons'
import { useUploadFlow } from '@/features/upload/composables/useUploadFlow'
import UploadMenu from '@/shared/components/upload-menu/UploadMenu.vue'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import { SAMPLE_MS, springCurve } from '@/shared/components/upload-menu/spring'

const props = withDefaults(
  defineProps<{
    notificationsOpen?: boolean
    hasUnread?: boolean
  }>(),
  {
    notificationsOpen: false,
    hasUnread: false,
  }
)

const emit = defineEmits<{
  activity: []
  navigate: []
}>()

const route = useRoute()
const router = useRouter()
const { showUploadDialog, canStartUpload } = useUploadFlow()
const aiEnabled = useFeatureFlag(FeatureFlag.AI)

const tabs = computed(() => [
  {
    key: 'home',
    label: 'Home',
    icon: HomeIcon,
    activeIcon: HomeFilledIcon,
    badge: false,
    action: () => {
      emit('navigate')
      router.push('/home')
    },
  },
  ...(aiEnabled.value
    ? [
        {
          key: 'docket',
          label: 'Docket',
          icon: FileDocketIcon,
          activeIcon: FileDocketFilledIcon,
          badge: false,
          action: () => {
            emit('navigate')
            router.push('/infringement')
          },
        },
      ]
    : []),
  {
    key: 'activity',
    label: 'Activity',
    icon: BellIcon,
    activeIcon: BellFilledIcon,
    badge: true,
    action: () => emit('activity'),
  },
])

const activeKey = computed(() => {
  if (props.notificationsOpen) return 'activity'
  if (aiEnabled.value && route.path.startsWith('/infringement')) return 'docket'
  if (route.path.startsWith('/home')) return 'home'
  return null
})

const activeIndex = computed(() =>
  tabs.value.findIndex((tab) => tab.key === activeKey.value)
)

const BAR_PADDING = 4
const SELECTION_WIDTH = 86
const TAB_PITCH = 74.5
const TAB_SPRING = { damping: 20, stiffness: 240, mass: 0.7 }

const barWidth = computed(
  () => BAR_PADDING * 2 + SELECTION_WIDTH + TAB_PITCH * (tabs.value.length - 1)
)

const PRESS_HOLD_MS = 1000

const capsule = ref<HTMLElement>()
const pressedIndex = ref<number | null>(null)
const displayIndex = computed(() => pressedIndex.value ?? activeIndex.value)
let releaseTimer: ReturnType<typeof setTimeout> | undefined

let target = 0
let curve: number[] = [0]
let curveStart = 0
let animation: Animation | null = null

const sampleAt = (now: number) => {
  const index = (now - curveStart) / SAMPLE_MS
  if (index >= curve.length - 1)
    return { position: curve[curve.length - 1], velocity: 0 }
  const i = Math.max(0, Math.floor(index))
  const step = curve[i + 1] - curve[i]
  return {
    position: curve[i] + step * (index - i),
    velocity: step / (SAMPLE_MS / 1000),
  }
}

const place = (position: number) => {
  if (capsule.value) capsule.value.style.transform = `translateX(${position}px)`
}

const springTo = (index: number) => {
  if (index < 0) return
  const next = index * TAB_PITCH
  if (next === target) return
  target = next
  const element = capsule.value
  if (!element) return
  const now = performance.now()
  const { position, velocity } = sampleAt(now)
  curve = springCurve(position, next, velocity, TAB_SPRING)
  curveStart = now
  animation?.cancel()
  place(next)
  animation = element.animate(
    curve.map((x) => ({ transform: `translateX(${x}px)` })),
    { duration: (curve.length - 1) * SAMPLE_MS, easing: 'linear' }
  )
}

const releasePress = () => {
  clearTimeout(releaseTimer)
  pressedIndex.value = null
  springTo(activeIndex.value)
}

const press = (index: number) => {
  clearTimeout(releaseTimer)
  pressedIndex.value = index
  springTo(index)
}

const awaitRoute = () => {
  clearTimeout(releaseTimer)
  releaseTimer = setTimeout(releasePress, PRESS_HOLD_MS)
}

const isInside = (event: PointerEvent) => {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  return (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  )
}

const handlePress = (event: PointerEvent, index: number) => {
  ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
  press(index)
}

const handleRelease = (event: PointerEvent, index: number) => {
  if (pressedIndex.value !== index) return
  if (!isInside(event)) return releasePress()
  awaitRoute()
  tabs.value[index]?.action()
}

const handleClick = (event: MouseEvent, index: number) => {
  if (event.detail !== 0) return
  press(index)
  awaitRoute()
  tabs.value[index]?.action()
}

watch(activeIndex, (index) => {
  springTo(index)
  if (index === pressedIndex.value) {
    clearTimeout(releaseTimer)
    pressedIndex.value = null
  }
})

onMounted(() => {
  target = Math.max(activeIndex.value, 0) * TAB_PITCH
  curve = [target]
  place(target)
})

onBeforeUnmount(() => {
  animation?.cancel()
  clearTimeout(releaseTimer)
})

const menuOpen = ref(false)
const fab = ref<HTMLButtonElement>()

const handleUpload = () => {
  if (aiEnabled.value) menuOpen.value = true
  else showUploadDialog()
}
</script>
